/**
 * Severity / risk styling helpers.
 *
 * Each function preserves the exact palette its consumers were inlining
 * before the Story 7.3 extraction — see Epic 7 Story 7.3 plan for
 * audit details. Palettes diverge across features (e.g. "Critical" is
 * `#EF4444` in vuln-mgmt but `#DC2626` in athena Results; "Low" is
 * blue `#60A5FA` in vuln-mgmt but green `#10B981` in athena), so callers
 * select their palette via the `scheme` parameter. Palette unification
 * is a separate UX-changing story for a future epic.
 */

// ────────────────────────────────────────────────────────────────────
// V1 — severity strings (Critical / High / Medium / Low)
// ────────────────────────────────────────────────────────────────────

const SEVERITY_PALETTES = {
  vulnMgmt: {
    CRITICAL: '#EF4444',
    HIGH:     '#F97316',
    MEDIUM:   '#FBBF24',
    LOW:      '#60A5FA',
    UNASSIGNED: '#9CA3AF',
    default:  '#9CA3AF',
  },
  athena: {
    CRITICAL: '#DC2626',
    HIGH:     '#EF4444',
    MEDIUM:   '#F59E0B',
    LOW:      '#10B981',
    default:  '#6B7280',
  },
  gapsCyan: {
    CRITICAL: '#EF4444',
    HIGH:     '#F59E0B',
    MEDIUM:   '#00D4FF',
    LOW:      '#10B981',
    default:  '#6B7280',
  },
};

/**
 * Normalize an arbitrary severity string to the canonical uppercase form.
 * Returns `'UNASSIGNED'` for non-string inputs.
 */
export function normalizeSeverity(severity) {
  return typeof severity === 'string' ? severity.toUpperCase() : 'UNASSIGNED';
}

/**
 * Return the color hex for a Critical/High/Medium/Low severity.
 * @param {string} severity - case-insensitive
 * @param {object} [opts]
 * @param {'vulnMgmt'|'athena'|'gapsCyan'} [opts.scheme='vulnMgmt']
 */
export function getSeverityColor(severity, { scheme = 'vulnMgmt' } = {}) {
  const palette = SEVERITY_PALETTES[scheme] ?? SEVERITY_PALETTES.vulnMgmt;
  return palette[normalizeSeverity(severity)] ?? palette.default;
}

/**
 * Map a CVSS score (0–10) to a severity color using the vulnMgmt palette.
 * Buckets: ≥9.0 Critical, ≥7.0 High, ≥4.0 Medium, >0.0 Low, else Unassigned.
 */
export function getCvssColor(score) {
  const palette = SEVERITY_PALETTES.vulnMgmt;
  if (score == null) return palette.default;
  if (score >= 9.0) return palette.CRITICAL;
  if (score >= 7.0) return palette.HIGH;
  if (score >= 4.0) return palette.MEDIUM;
  if (score > 0.0)  return palette.LOW;
  return palette.default;
}

/**
 * Map an EPSS score (0–1) to a severity color using the vulnMgmt palette.
 * Buckets: ≥0.9 Critical, ≥0.7 High, ≥0.4 Medium, >0.0 Low, else Unassigned.
 */
export function getEpssColor(score) {
  const palette = SEVERITY_PALETTES.vulnMgmt;
  if (score == null) return palette.default;
  if (score >= 0.9) return palette.CRITICAL;
  if (score >= 0.7) return palette.HIGH;
  if (score >= 0.4) return palette.MEDIUM;
  if (score > 0.0)  return palette.LOW;
  return palette.default;
}

// ────────────────────────────────────────────────────────────────────
// V2 — CAL levels (CAL1 .. CAL4)
// ────────────────────────────────────────────────────────────────────

const CAL_PALETTES = {
  // Canonical CAL palette — used by Step 7, Step 8, the admin
  // CALMatrixEditor, the legacy results VV recommendations, and the
  // CybersecurityGoalsSection. Single source of truth so the colour
  // a user sees on a CAL chip is the same everywhere.
  standard: {
    CAL4: '#DC2626', CAL3: '#F97316', CAL2: '#F59E0B', CAL1: '#10B981', default: '#6B7280',
  },
};

/**
 * Return the color hex for a CAL level.
 * @param {string} cal - 'CAL1' | 'CAL2' | 'CAL3' | 'CAL4'
 * @param {object} [opts]
 * @param {'standard'} [opts.scheme='standard']
 */
export function getCALColor(cal, { scheme = 'standard' } = {}) {
  const palette = CAL_PALETTES[scheme] ?? CAL_PALETTES.standard;
  return palette[cal] ?? palette.default;
}

// V&V Recommendations Modal needs a compound { bg, border, text } shape.
const VV_MODAL_CAL_STYLES = {
  CAL1: { bg: 'rgba(16, 185, 129, 0.2)', border: '#10B981', text: '#10B981' },
  CAL2: { bg: 'rgba(245, 158, 11, 0.2)', border: '#F59E0B', text: '#F59E0B' },
  CAL3: { bg: 'rgba(249, 115, 22, 0.2)', border: '#F97316', text: '#F97316' },
  CAL4: { bg: 'rgba(239, 68, 68, 0.2)',  border: '#EF4444', text: '#EF4444' },
};

/**
 * Return a compound `{ bg, border, text }` style triple for a CAL level.
 * Currently only supports the 'vvModal' scheme used by VVRecommendationsModal.
 */
export function getCALStyle(cal, { scheme = 'vvModal' } = {}) {
  if (scheme === 'vvModal') return VV_MODAL_CAL_STYLES[cal] ?? null;
  return null;
}

// ────────────────────────────────────────────────────────────────────
// V3 — impact ratings (Severe / Major / Moderate / Minor / Negligible)
// ────────────────────────────────────────────────────────────────────

const IMPACT_PALETTES = {
  fiveTier: {
    Severe: '#DC2626', Major: '#EF4444', Moderate: '#F59E0B', Minor: '#34D399', Negligible: '#10B981',
    default: '#6B7280',
  },
  fourTier: {
    Severe: '#ef4444', Major: '#f97316', Moderate: '#eab308', Negligible: '#10b981',
    default: '#6b7280',
  },
  iosTones: {
    Severe: '#FF3B30', Major: '#FF9500', Moderate: '#FFCC00', Negligible: '#34C759',
    default: '#fff',
  },
};

// ────────────────────────────────────────────────────────────────────
// V3.5 — attack feasibility (Very Low / Low / Medium / High)
// ────────────────────────────────────────────────────────────────────

/** Per ISO/SAE 21434 Annex G, attack feasibility uses the four-tier
 *  scale Very Low / Low / Medium / High. Higher = easier for the
 *  attacker (i.e. worse for defenders), so the palette inverts
 *  intuition: green = hard to attack, red = trivial. */
/* Canonical attack feasibility palette — four-tier scale Very Low /
   Low / Medium / High. Higher = easier for the attacker (worse for
   defenders), so the palette inverts intuition: green = hard to
   attack, red = trivial. Used by Step 5 attack-path tables, the
   workflow-versions resolver, and the admin Thresholds editor. */
const FEASIBILITY_PALETTE = {
  'Very Low': '#10b981', // green   — defender's best case
  'Low': '#eab308',      // yellow
  'Medium': '#f97316',   // orange
  'High': '#ef4444',     // red    — defender's worst case
  default: '#6b7280',
};

/**
 * Return the color hex for an attack feasibility rating.
 * @param {string} rating - 'Very Low' | 'Low' | 'Medium' | 'High'
 */
export function getFeasibilityColor(rating) {
  return FEASIBILITY_PALETTE[rating] ?? FEASIBILITY_PALETTE.default;
}

// ────────────────────────────────────────────────────────────────────
// V3.6 — risk treatment (Avoid / Reduce / Share / Retain)
//         + treatment priority (Immediate / High / Medium / Low)
//         + risk level (Low / Medium / High)
// ────────────────────────────────────────────────────────────────────

/** Per ISO 21434 Clause 9.3.4, the treatment decision is the
 *  defender's response. Two schemes:
 *  - `standard` codes the *severity of action required*: Avoid is
 *    the most disruptive (had to abandon a feature), Retain is the
 *    calmest (accept and monitor). Used by admin-config + the
 *    workflow-versions risk fields.
 *  - `map` is the ReactFlow traceability-map's per-treatment-decision
 *    edge tint. Carries different semantics (visual contrast in a
 *    DAG context) — kept as a separate scheme so the map's existing
 *    visual is preserved while still routing through this util. */
const TREATMENT_DECISION_PALETTES = {
  standard: {
    Avoid: '#ef4444',  // red    — most disruptive
    Reduce: '#f97316', // orange — mitigation required
    Share: '#eab308',  // yellow — transferred to third party
    Retain: '#10b981', // green  — accepted as-is
    default: '#6b7280',
  },
  map: {
    Reduce: '#10B981',
    Share:  '#3B82F6',
    Retain: '#9CA3AF',
    Avoid:  '#EF4444',
    default: '#6b7280',
  },
};

/**
 * Return the color for a treatment decision.
 * @param {string} decision - 'Avoid' | 'Reduce' | 'Share' | 'Retain'
 * @param {object} [opts]
 * @param {'standard'|'map'} [opts.scheme='standard']
 */
export function getTreatmentDecisionColor(decision, { scheme = 'standard' } = {}) {
  const palette = TREATMENT_DECISION_PALETTES[scheme] ?? TREATMENT_DECISION_PALETTES.standard;
  return palette[decision] ?? palette.default;
}

/** Ordered list of treatment-decision enum values — for filter chips,
 *  legend rendering, etc. Matches ISO 21434 Clause 9.3.4 vocabulary. */
export const TREATMENT_DECISIONS = ['Avoid', 'Reduce', 'Share', 'Retain'];

/** Treatment priority — Immediate / High / Medium / Low. Higher
 *  priority = redder. */
const TREATMENT_PRIORITY_PALETTE = {
  Immediate: '#dc2626', // dark red
  High: '#ef4444',
  Medium: '#f97316',
  Low: '#10b981',
  default: '#6b7280',
};

export function getTreatmentPriorityColor(priority) {
  return TREATMENT_PRIORITY_PALETTE[priority] ?? TREATMENT_PRIORITY_PALETTE.default;
}

/** Risk level — three-tier text Low / Medium / High. Used by the
 *  risk_assessment_agent alongside the numeric risk_value. */
const RISK_LEVEL_PALETTE = {
  Low: '#10b981',     // green
  Medium: '#f59e0b',  // amber
  High: '#ef4444',    // red
  default: '#6b7280',
};

export function getRiskLevelColor(level) {
  return RISK_LEVEL_PALETTE[level] ?? RISK_LEVEL_PALETTE.default;
}

/**
 * Return the color hex for an impact rating.
 * @param {string} rating - 'Severe' | 'Major' | 'Moderate' | 'Minor' | 'Negligible'
 * @param {object} [opts]
 * @param {'fiveTier'|'fourTier'|'iosTones'} [opts.scheme='fiveTier']
 */
export function getImpactColor(rating, { scheme = 'fiveTier' } = {}) {
  const palette = IMPACT_PALETTES[scheme] ?? IMPACT_PALETTES.fiveTier;
  return palette[rating] ?? palette.default;
}

// ────────────────────────────────────────────────────────────────────
// V4 — numeric risk values (1..5)
// ────────────────────────────────────────────────────────────────────

const RISK_VALUE_PALETTES = {
  // Canonical risk-value palette — value 1 is light green (Very Low),
  // value 2 is darker green (Low), then yellow / orange / red as the
  // values escalate. Used by Step 6 RiskMatrix, workflow-versions, and
  // the admin-config RiskMatrixEditor so the colors a user picks
  // appear identically in every analysis surface.
  standard: { 5: '#e53e3e', 4: '#ed8936', 3: '#ecc94b', 2: '#38a169', 1: '#48bb78' },
};

const RGB_FROM_HEX = {
  '#e53e3e': '229, 62, 62',
  '#ed8936': '237, 137, 54',
  '#ecc94b': '236, 201, 75',
  '#48bb78': '72, 187, 120',
  '#38a169': '56, 161, 105',
};

/**
 * Return the color for a numeric risk value (1..5).
 * When `alpha < 1`, returns an `rgba(...)` string with the matching channel
 * triple (formatted with single-space-after-comma to match consumer code).
 * @param {number} value - 1..5
 * @param {object} [opts]
 * @param {'standard'} [opts.scheme='standard']
 * @param {number} [opts.alpha=1] - 0..1
 */
export function getRiskValueColor(value, { scheme = 'standard', alpha = 1 } = {}) {
  const palette = RISK_VALUE_PALETTES[scheme] ?? RISK_VALUE_PALETTES.standard;
  const hex = palette[value];
  if (!hex) return null;
  if (alpha >= 1) return hex;
  const rgb = RGB_FROM_HEX[hex];
  return rgb ? `rgba(${rgb}, ${alpha})` : hex;
}

// ────────────────────────────────────────────────────────────────────
// V5 — numeric risk score (Athena overview)
// ────────────────────────────────────────────────────────────────────

/**
 * Map an Athena risk score to a color hex.
 * Buckets: ≤3 green, ≤5 amber, ≤7 red, >7 dark red.
 */
export function getRiskScoreColor(score) {
  if (score <= 3) return '#10B981';
  if (score <= 5) return '#F59E0B';
  if (score <= 7) return '#EF4444';
  return '#DC2626';
}

// ────────────────────────────────────────────────────────────────────
// V6 — STRIDE categories
//   Canonical per-STRIDE-category colour + label. Sourced from the
//   ThreatTraceabilityMap's previous local STRIDE_COLOR map so that
//   the map's visual is preserved when it rewires onto this util.
// ────────────────────────────────────────────────────────────────────

const STRIDE_PALETTES = {
  standard: {
    SPOOFING:                '#A855F7',
    TAMPERING:               '#EF4444',
    REPUDIATION:             '#F59E0B',
    INFORMATION_DISCLOSURE:  '#3B82F6',
    DENIAL_OF_SERVICE:       '#EA580C',
    ELEVATION_OF_PRIVILEGE:  '#DC2626',
    default:                 '#6b7280',
  },
};

const STRIDE_LABELS = {
  SPOOFING:                'Spoofing',
  TAMPERING:               'Tampering',
  REPUDIATION:             'Repudiation',
  INFORMATION_DISCLOSURE:  'Information Disclosure',
  DENIAL_OF_SERVICE:       'Denial of Service',
  ELEVATION_OF_PRIVILEGE:  'Elevation of Privilege',
};

/**
 * Return the color for a STRIDE category.
 * @param {string} category — uppercase enum, e.g. 'SPOOFING'
 * @param {object} [opts]
 * @param {'standard'} [opts.scheme='standard']
 */
export function getStrideColor(category, { scheme = 'standard' } = {}) {
  const palette = STRIDE_PALETTES[scheme] ?? STRIDE_PALETTES.standard;
  return palette[category] ?? palette.default;
}

/** Human-readable label for a STRIDE category — title-cased per
 *  the canonical map. Returns the input verbatim if unmapped. */
export function getStrideLabel(category) {
  return STRIDE_LABELS[category] ?? category ?? '';
}

/** Ordered list of STRIDE category enum values — for filter chips,
 *  legend rendering, etc. */
export const STRIDE_CATEGORIES = Object.keys(STRIDE_LABELS);

// ────────────────────────────────────────────────────────────────────
// V7 — Entity-type / column colours
//   Per-entity-type (threat / asset / damage / attack / risk / goal /
//   claim / control / requirement) accent colour. Used by the
//   ThreatTraceabilityMap's column legend + edge tints, and now by
//   the 2.0 traceability table's Asset CategoryPill + chain chips.
//   Sourced from the map's previous local COLUMN_COLOR map.
// ────────────────────────────────────────────────────────────────────

const ENTITY_TYPE_PALETTES = {
  standard: {
    threat:      '#F59E0B',
    asset:       '#00D4FF',
    damage:      '#EF4444',
    attack:      '#EA580C',
    risk:        '#A855F7',
    goal:        '#10B981',
    claim:       '#3B82F6',
    control:     '#14B8A6',
    requirement: '#6366F1',
    default:     '#6b7280',
  },
};

const ENTITY_TYPE_LABELS = {
  threat:      'Threat',
  asset:       'Asset',
  damage:      'Damage',
  attack:      'Attack',
  risk:        'Risk',
  goal:        'Goal',
  claim:       'Claim',
  control:     'Control',
  requirement: 'Requirement',
};

/**
 * Return the color for an entity type.
 * @param {string} kind — lowercase, e.g. 'threat' | 'asset' | 'damage'
 *                         | 'attack' | 'risk' | 'goal' | 'claim' | 'control'
 *                         | 'requirement'
 * @param {object} [opts]
 * @param {'standard'} [opts.scheme='standard']
 */
export function getEntityTypeColor(kind, { scheme = 'standard' } = {}) {
  const palette = ENTITY_TYPE_PALETTES[scheme] ?? ENTITY_TYPE_PALETTES.standard;
  return palette[kind] ?? palette.default;
}

/** Human-readable label for an entity type. Returns the input
 *  verbatim if unmapped. */
export function getEntityTypeLabel(kind) {
  return ENTITY_TYPE_LABELS[kind] ?? kind ?? '';
}

// ────────────────────────────────────────────────────────────────────
// V8 — Canonical label accessors for vocabularies that consumers
//      previously capitalised ad-hoc (severity uppercase enum,
//      treatment decision, impact ratings, feasibility ratings, CAL).
//      Use these in place of `value.charAt(0) + value.slice(1).toLowerCase()`.
// ────────────────────────────────────────────────────────────────────

const SEVERITY_LABELS = {
  CRITICAL:   'Critical',
  HIGH:       'High',
  MEDIUM:     'Medium',
  LOW:        'Low',
  UNASSIGNED: 'Unassigned',
};

/** Return the title-cased display label for a severity enum value
 *  (data comes uppercase from the backend). */
export function getSeverityLabel(severity) {
  return SEVERITY_LABELS[normalizeSeverity(severity)] ?? severity ?? '';
}

/** Display label for an impact rating. Backend already title-cases
 *  these (e.g. 'Severe', 'Major'), so the accessor returns the
 *  input verbatim — but routing through here makes future
 *  reformatting (e.g. localisation) a one-line change. */
export function getImpactLabel(rating) {
  return rating ?? '';
}

/** Display label for an attack-feasibility rating. */
export function getFeasibilityLabel(rating) {
  return rating ?? '';
}

/** Display label for a treatment decision. Data is title-cased
 *  already (Avoid / Reduce / Share / Retain). */
export function getTreatmentDecisionLabel(decision) {
  return decision ?? '';
}

/** Display label for a CAL level (CAL1..CAL4). Returns the input
 *  verbatim — exists so consumers can route through one accessor
 *  for all level vocabularies. */
export function getCALLabel(cal) {
  return cal ?? '';
}
